/**
 * Botón CUADRADO de ∂iario aventureros — el de la puerta: quien todavía no está dentro (login,
 * salidas de unirse sin cuenta, puertas de crear experiencia). Dentro de la app manda `Button`,
 * el redondeado de 12px — regla **cuadrado = la puerta · redondeado = la casa**
 * (`docs/design-system.md` § Reglas de marca).
 *
 * Puerto TSX + Tailwind del contrato del cliente `docs/design/bundle/2026-09-02-fundacion/
 * components/buttons/SquareButton.d.ts` (props) y su CSS de referencia `SquareButton.jsx`
 * (constante `SQBTN_CSS`, no se importa: se reescribe sobre los tokens de `styles/tokens.css`
 * expuestos por `styles/index.css`). Radio 3px, MAYÚSCULAS, Roboto 500 con tracking `.04em`,
 * `min-h-11` (44px) de área táctil.
 *
 * Dos mecánicas de sombra, iguales que el contrato:
 * - `chunky` (por defecto `true`): sombra sólida de 3px (color según variante) que se hunde a 1px
 *   con `translateY(2px)` al pulsar, y se oscurece con `brightness(.95)` al pasar el ratón.
 * - `chunky={false}`: el plano del embudo — sin sombra, hover a opacidad .85, activo a .75.
 *
 * `href`/`type` no están en el `.d.ts` del cliente (que solo extiende `HTMLAttributes<HTMLElement>`,
 * sin atributos propios de `<a>`/`<button>` — el `.jsx` de origen los deja pasar sin tipar por
 * `...rest`): se añaden aquí para que `as='a'` con `href` y el `type='button'` por defecto (evita
 * el submit implícito de un `<button>` dentro de un `<form>`, mismo criterio que `Button`) tipen
 * correctamente. El resto de atributos nativos (`onClick`, `id`, `aria-*`…) llega por `...resto`.
 *
 * `disabled` no existe como atributo nativo en `<a>`: con `as='a'` se traduce a
 * `aria-disabled='true'` + `tabIndex={-1}` (fuera del orden de tabulación), y el CSS de
 * `disabled:`/`aria-disabled:` de `clasesDeBotonCuadrado` cubre ambos casos con el mismo aspecto.
 */
export interface BotonCuadradoProps extends React.HTMLAttributes<HTMLElement> {
	/** Etiqueta a renderizar. `'a'` para enlaces (wa.me, calendario) — usa `href`. */
	as?: 'button' | 'a';
	children?: React.ReactNode;
	/** Sombra inferior sólida de 3px que se hunde al pulsar. `false` = el plano del embudo. */
	chunky?: boolean;
	disabled?: boolean;
	/** Ocupa todo el ancho disponible (CTA de puerta en 375px). */
	full?: boolean;
	/** Solo tiene efecto con `as='a'`. */
	href?: string;
	/** Nodo o emoji a la izquierda del texto. */
	icon?: React.ReactNode;
	/** `md` es exactamente el del embudo (13px 32px, 14px). */
	size?: 'sm' | 'md' | 'lg';
	/** Solo tiene efecto con `as='button'` (por defecto). */
	type?: 'button' | 'reset' | 'submit';
	/** `primary` lima (defecto) · `ink` tinta cálida · `ghost` solo borde · `member` magenta ·
	 * `grumete` amarillo. */
	variant?: 'primary' | 'ink' | 'ghost' | 'member' | 'grumete';
}

type VarianteBotonCuadrado = NonNullable<BotonCuadradoProps['variant']>;
type TamanoBotonCuadrado = NonNullable<BotonCuadradoProps['size']>;

interface ClasesBotonCuadradoParams {
	chunky?: boolean;
	full?: boolean;
	size?: TamanoBotonCuadrado;
	variant?: VarianteBotonCuadrado;
}

/** Estructura común a las cinco variantes y los tres tamaños: tipografía, radio 3px, área táctil
 * mínima y la transición (`motion-safe:`, se apaga entera con reduced-motion, igual que el
 * `@media (prefers-reduced-motion: reduce)` del CSS de origen). Sin color: cada variante pone el
 * suyo, para que dos utilidades de Tailwind nunca compitan por la misma propiedad. */
const CLASES_BASE =
	'inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[3px] border border-transparent font-body font-medium uppercase tracking-[.04em] leading-none no-underline motion-safe:transition-[opacity,filter,border-color,transform,box-shadow] motion-safe:duration-fast motion-safe:ease focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-45 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-45';

/** `md` es literalmente el botón del embudo: `13px 32px`, `14px`. `sm`/`lg` no caen en la escala
 * de espaciado de 4px del sistema (18px, 10px, 13px no son múltiplos), de ahí los valores entre
 * corchetes; `32px`/`16px`/`40px` sí son múltiplos y usan las utilidades `px-8`/`py-4`/`px-10`. */
const CLASES_TAMANO: Record<TamanoBotonCuadrado, string> = {
	lg: 'px-10 py-4 text-[15px]',
	md: 'px-8 py-[13px] text-[14px]',
	sm: 'px-[18px] py-[10px] text-[12px]',
};

/** Color de fondo, texto y — solo en `ghost` — borde, más el color del anillo de foco: tinta
 * cálida sobre lima/amarillo, lima sobre tinta/magenta (regla de foco del `.d.ts`/card de
 * `buttons-cuadrados`). `ghost` oscurece su borde al 25 % → 100 % en hover, igual con o sin
 * `chunky` (el CSS de origen no lo condiciona a `.is-chunky`). */
const CLASES_VARIANTE: Record<VarianteBotonCuadrado, string> = {
	ghost: 'border-ink-warm/25 bg-transparent text-ink-warm hover:border-ink-warm focus-visible:outline-ink-warm',
	grumete: 'bg-grumete text-ink-warm focus-visible:outline-ink-warm',
	ink: 'bg-ink-warm text-text-on-dark focus-visible:outline-accent',
	member: 'bg-member text-text-on-dark focus-visible:outline-accent',
	primary: 'bg-accent text-ink-warm focus-visible:outline-ink-warm',
};

/**
 * Sombra `chunky`: 3px sólidos que se hunden a 1px con `translateY(2px)` al pulsar, y
 * `brightness(.95)` al pasar el ratón — igual que `.is-chunky` en `SquareButton.jsx`. El color de
 * cada sombra (`--_press` en el CSS de origen) es copia literal de esa misma constante: solo
 * `primary` tiene token propio (`--da-accent-shadow`, de `tokens.css`); las otras cuatro no lo
 * tienen — el cliente las deja como `rgba(...)` sueltos dentro del componente — así que se portan
 * igual, como valor arbitrario de Tailwind en vez de inventar un token que el design system no
 * declara.
 */
const CLASES_CHUNKY: Record<VarianteBotonCuadrado, string> = {
	ghost:
		'shadow-[0_3px_0_rgba(47,36,24,.12)] hover:brightness-95 active:translate-y-[2px] active:shadow-[0_1px_0_rgba(47,36,24,.12)]',
	grumete:
		'shadow-[0_3px_0_rgba(200,160,0,.35)] hover:brightness-95 active:translate-y-[2px] active:shadow-[0_1px_0_rgba(200,160,0,.35)]',
	ink: 'shadow-[0_3px_0_rgba(47,36,24,.35)] hover:brightness-95 active:translate-y-[2px] active:shadow-[0_1px_0_rgba(47,36,24,.35)]',
	member:
		'shadow-[0_3px_0_rgba(180,0,170,.3)] hover:brightness-95 active:translate-y-[2px] active:shadow-[0_1px_0_rgba(180,0,170,.3)]',
	primary:
		'shadow-[0_3px_0_var(--da-accent-shadow)] hover:brightness-95 active:translate-y-[2px] active:shadow-[0_1px_0_var(--da-accent-shadow)]',
};

/** `chunky={false}`: el CSS literal del embudo — sin sombra, hover a opacidad .85, activo a .75. */
const CLASES_PLANO = 'hover:opacity-85 active:opacity-75';

/**
 * Clases de un botón cuadrado, pura (mismos argumentos → mismo resultado, sin tocar el DOM):
 * la usa `BotonCuadrado` por dentro y cualquier `<Link>` del router que necesite vestirse de
 * botón cuadrado sin serlo (p. ej. una navegación real con `@tanstack/react-router`).
 */
export function clasesDeBotonCuadrado({
	chunky = true,
	full = false,
	size = 'md',
	variant = 'primary',
}: ClasesBotonCuadradoParams = {}): string {
	return [
		CLASES_BASE,
		CLASES_TAMANO[size],
		CLASES_VARIANTE[variant],
		chunky ? CLASES_CHUNKY[variant] : CLASES_PLANO,
		full ? 'w-full' : '',
	]
		.filter(Boolean)
		.join(' ');
}

export const BotonCuadrado = ({
	as = 'button',
	children,
	chunky = true,
	className = '',
	disabled = false,
	full = false,
	href,
	icon = null,
	size = 'md',
	type = 'button',
	variant = 'primary',
	...resto
}: BotonCuadradoProps): React.JSX.Element => {
	const clases = `${clasesDeBotonCuadrado({ chunky, full, size, variant })} ${className}`.trim();
	const nodoIcono = icon ? (
		<span aria-hidden='true' className='inline-flex items-center text-[1.1em] leading-[0]'>
			{icon}
		</span>
	) : null;

	if (as === 'a') {
		return (
			<a
				aria-disabled={disabled || undefined}
				className={clases}
				href={href}
				tabIndex={disabled ? -1 : undefined}
				{...resto}
			>
				{nodoIcono}
				{children}
			</a>
		);
	}

	return (
		<button className={clases} disabled={disabled} type={type} {...resto}>
			{nodoIcono}
			{children}
		</button>
	);
};
